import { Suspense, useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import PageLayout from "@layouts/PageLayout/PageLayout";

import PageFooter from "@components/common/PageFooter";
import PageHeader from "@components/common/PageHeader";
import LoadingInterstitial from "@components/LoadingInterstitial";
import { MemberAccessBoundary } from "@components/MemberAccessBoundary";
import { RequireTechniquesNamespace } from "@components/RequireTechniquesNamespace";
import { simulationsEnabled } from "@components/Simulation/useSimulationVisibility";

// Page imports
import { Auth } from "@lib/authentication/amplify/amplify";
import { parseAuthResult } from "@lib/authentication/amplify/amplify.helpers";
import { RequireAuth } from "@lib/authentication/components/RequireAuth";
import { lazyWithReload } from "@lib/chunkReload";
import { AuthenticationProvider } from "@lib/contexts/AuthenticationContext";
import { NmhProvider } from "@lib/contexts/NmhContext";
import { VideoProvider } from "@lib/contexts/VideoContextOld.tsx";
import {
  AUTH_ROUTES,
  localePrefixed,
  LOGIN_ROUTE,
  LOGOUT_ROUTES,
  MEMBER_ROUTES,
  PUBLIC_ROUTES,
} from "@lib/routePaths";
// Checkout stays eager: it's the LCP-critical route and any Suspense
// fallback on first mount would compete with the skeleton-handoff work in
// vite/index.html. Every other page is lazy so /checkout visitors don't
// download member-area code (and vice versa).
import Checkout from "@pages/checkout/checkout";
// Variant arm of the PaymentFirstCheckout A/B test (OMG-1916). Eager like
// Checkout: it's the same LCP-critical surface, and lazy-loading only one arm
// would add a Suspense-fallback treatment asymmetry to the experiment.
import { CheckoutPaymentFirst } from "@pages/checkout/CheckoutPaymentFirst";
import { leanBackEnabled } from "@pages/collections/leanback/flags";

import { routeChunks } from "./routeChunks";

/**
 * Locale-prefixed gift URLs (e.g. /de/gifts, /es_419/redeem) still exist in the
 * wild — the marketing-site footer, older gift emails, bookmarks — from when the
 * legacy stack served the gift flow under a locale path. This SPA has no
 * /:lng/* routes and localizes via ?lng=, so those URLs matched nothing and
 * rendered a blank page (OMG-2064). Redirect them to the canonical path,
 * carrying the path locale into ?lng and preserving any existing query
 * (o=, utm_*, etc.). The legacy #/give hash is dropped (unused by this app).
 */
const LocaleGiftRedirect = ({ to }: { to: string }) => {
  const { lng } = useParams();
  const { search } = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (lng) params.set("lng", lng);
    const qs = params.toString();
    // Full-document redirect (window.location, not a client-side <Navigate>) on
    // purpose: i18n's LanguageDetector and LocaleProvider only read ?lng at
    // initial mount (empty-dep useEffect), so the target must load fresh with
    // ?lng in the URL. A client-side hop would leave the give page in the wrong
    // language (cookie/localStorage default) — defeating the fix on the very
    // non-English pages this targets. replace() so /:lng/gifts leaves no history
    // entry. (OMG-2064)
    window.location.replace(qs ? `${to}?${qs}` : to);
  }, [to, lng, search]);
  return null;
};

/**
 * The catch-all's redirect to /home, remounted on every location change so it
 * fires again if its redirect is undone. That happens on the first commit when
 * the URL carries ?lng=: LocaleProvider strips it with a navigation resolved
 * against the path it rendered with, which runs after this redirect (passive
 * effects run child first) and puts the unknown path back. A plain <Navigate>
 * has nothing new to react to after that and never fires again: a blank page
 * (OMG-2676). /home always matches a real route, so this cannot loop.
 */
const CatchAllRedirect = () => {
  const { key } = useLocation();
  return <Navigate key={key} to={MEMBER_ROUTES.HOME} replace />;
};

// The import() thunks live in routeChunks.ts so main.tsx can start the one
// the current URL needs before React mounts (OMG-1836); wrapping the same
// thunk here means lazy() finds the module already in flight or resolved.
const ArticlePage = lazyWithReload(routeChunks.ArticlePage);
const AuthPage = lazyWithReload(routeChunks.AuthPage);
const WhoAmIPage = lazyWithReload(routeChunks.WhoAmIPage);
const PostCheckout = lazyWithReload(routeChunks.PostCheckout);
const CollectionPage = lazyWithReload(routeChunks.CollectionPage);
const TechniquePageContainer = lazyWithReload(
  routeChunks.TechniquePageContainer,
);
const TechniquePageLayout = lazyWithReload(routeChunks.TechniquePageLayout);
const LeanBackView = lazyWithReload(routeChunks.LeanBackView);
const DateNightPage = lazyWithReload(routeChunks.DateNightPage);
const GiftCard = lazyWithReload(routeChunks.GiftCard);
const GiftPage = lazyWithReload(routeChunks.GiftPage);
const NewMemberHome = lazyWithReload(routeChunks.NewMemberHome);
const Redeem = lazyWithReload(routeChunks.Redeem);
const SimulationPage = lazyWithReload(routeChunks.SimulationPage);
const UpsellPage = lazyWithReload(routeChunks.UpsellPage);

/** QA-only routes are limited to known non-production stages. Missing or
 *  unknown stage names fail closed. Read at render, not at module load, so the
 *  route-table test can stub the stage. */
const isQaStage = () =>
  /^(uat|foxhole|local)$/i.test(import.meta.env.VITE_ENV ?? "");

// Lean-back mode (OMG-2747) behind its release switch: with the switch off
// the watch URL lands on the technique page instead of a dark screen.
const LeanBackRoute = () => {
  const { id } = useParams<{ id: string }>();
  if (!leanBackEnabled()) {
    return <Navigate to={`${MEMBER_ROUTES.TECHNIQUES}/${id ?? ""}`} replace />;
  }
  return <LeanBackView />;
};

const Index = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const queryParams = location.search;

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const result = parseAuthResult(await Auth.currentAuthenticatedUser());
        setIsAuthenticated(!!result);
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (location.pathname !== "/") {
    return <Outlet />;
  }

  if (isLoading) {
    return <LoadingInterstitial />;
  }

  return (
    <Navigate
      to={
        isAuthenticated
          ? `${MEMBER_ROUTES.HOME}${queryParams}`
          : `${LOGIN_ROUTE}${queryParams}`
      }
    />
  );
};

export const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingInterstitial />}>
      <Routes>
        <Route index element={<Index />} />

        {/* Public routes — no auth required */}
        <Route path={PUBLIC_ROUTES.GIFTS} element={<GiftPage />} />
        <Route path={PUBLIC_ROUTES.GIFTS_CARD} element={<GiftCard />} />
        <Route path={PUBLIC_ROUTES.REDEEM} element={<Redeem />} />
        {/* Locale-prefixed gift URLs (e.g. /de/gifts, /es_419/redeem) from the
            marketing footer / older gift emails → canonical path with ?lng.
            Without these they matched no route and rendered blank (OMG-2064). */}
        <Route
          path={localePrefixed(PUBLIC_ROUTES.GIFTS)}
          element={<LocaleGiftRedirect to={PUBLIC_ROUTES.GIFTS} />}
        />
        <Route
          path={localePrefixed(PUBLIC_ROUTES.GIFTS_CARD)}
          element={<LocaleGiftRedirect to={PUBLIC_ROUTES.GIFTS_CARD} />}
        />
        <Route
          path={localePrefixed(PUBLIC_ROUTES.REDEEM)}
          element={<LocaleGiftRedirect to={PUBLIC_ROUTES.REDEEM} />}
        />
        <Route path={PUBLIC_ROUTES.CHECKOUT} element={<Checkout />} />
        {/* Distinct variant URL (nested under /checkout/ so it inherits the
            public basename + checkout perf/analytics path predicates). */}
        <Route
          path={PUBLIC_ROUTES.CHECKOUT_PAYMENT_FIRST}
          element={<CheckoutPaymentFirst />}
        />

        {/* Routes with no header/footer */}
        {/* VideoProvider used to live at the App level in main.tsx
            but its only runtime consumer is VideoPlayerOld, which
            is rendered inside ArticleVideo/ArticleAnimation/
            CollectionPage/DateNightPage — all member-area routes
            reachable only through this block. Wrapping here keeps
            the provider scoped to where it's actually needed and
            removes a static import (and its small JS execution
            cost) from /checkout, /gifts, /redeem, /post-checkout,
            and the /login auth routes. */}
        <Route
          path=""
          element={
            <AuthenticationProvider>
              <RequireAuth>
                <NmhProvider>
                  <VideoProvider>
                    <PageLayout hideHeader hideFooter />
                  </VideoProvider>
                </NmhProvider>
              </RequireAuth>
            </AuthenticationProvider>
          }
        >
          {/* techniques.json left the i18n init list (OMG-1836). Everything
              in this pathless route reads it through fallbackNS, so the gate
              holds the tree until it has landed; main.tsx starts the
              download with the entry chunk, so by the time auth and getMe
              resolve it is normally already here and this renders straight
              through. /post-checkout stays outside: it only reads
              checkoutPage.* keys and then hard-reloads into /members/. Keep
              routeChunks.needsTechniquesNamespace in sync with what sits
              here. */}
          <Route
            element={
              <RequireTechniquesNamespace>
                <Outlet />
              </RequireTechniquesNamespace>
            }
          >
            {/* A failed access check must not mount Preview/upgrade
                decisions. Receipts and account diagnostics remain reachable
                below. */}
            <Route element={<MemberAccessBoundary />}>
              <Route
                path={MEMBER_ROUTES.COLLECTION}
                element={<CollectionPage />}
              />
              <Route
                path={MEMBER_ROUTES.DATE_NIGHT}
                element={<DateNightPage />}
              />
              <Route path={MEMBER_ROUTES.ARTICLE} element={<ArticlePage />} />
              <Route
                path={`${MEMBER_ROUTES.HOME}/:locale?`}
                element={<NewMemberHome />}
              />
              <Route path={MEMBER_ROUTES.UPSELL} element={<UpsellPage />} />
              <Route
                path={MEMBER_ROUTES.TECHNIQUES}
                element={<TechniquePageLayout />}
              >
                <Route
                  path={MEMBER_ROUTES.TECHNIQUE}
                  element={<TechniquePageContainer />}
                />
                <Route
                  path={MEMBER_ROUTES.TECHNIQUE_WATCH}
                  element={<LeanBackRoute />}
                />
              </Route>
            </Route>
            {/* Debug dump of the member's token claims: QA stages only. */}
            {isQaStage() && (
              <Route path={MEMBER_ROUTES.WHOAMI} element={<WhoAmIPage />} />
            )}
            {/* QA is opt-in and limited to known non-production stages. The
                component also requires the member's explicit-content
                preference. */}
            {simulationsEnabled() && isQaStage() && (
              <Route
                path={MEMBER_ROUTES.SIMULATION_TEST}
                element={<SimulationPage />}
              />
            )}
          </Route>
          <Route
            path={MEMBER_ROUTES.POST_CHECKOUT}
            element={<PostCheckout />}
          />
        </Route>

        {/* Auth routes with header and footer */}
        {AUTH_ROUTES.map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <>
                <PageHeader />
                <AuthPage />
                <PageFooter />
              </>
            }
          />
        ))}

        {/* Logout routes */}
        {LOGOUT_ROUTES.map((path) => (
          <Route key={path} path={path} element={<AuthPage />} />
        ))}

        {/* Fallback route */}
        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
    </Suspense>
  );
};
